import { useState } from "react";
import { Plus, Trash2 } from "lucide-react";

import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import { Select } from "@/components/ui/Select";

interface Entry { key: string; value: string; type: "string" | "number" | "boolean" | "json"; }

interface Props {
  initialValues?: Record<string, unknown>;
  onSubmit: (values: Record<string, unknown>, description: string) => void;
  loading?: boolean;
}

const KNOWN_KEYS = [
  "qualification.required_points",
  "club_entry.max_per_user",
  "placement.max_direct_children",
  "placement.strategy",
  "placement.max_retries",
  "placement.reservation_ttl_seconds",
  "stage.count",
  "stage.required_units",
  "ancestor_progress.max_depth",
  "ancestor_progress.unit_per_descendant",
  "auth.session_policy",
  "reentry.max_count_per_user",
  "wallet.withdrawal_min",
  "wallet.withdrawal_max",
  "wallet.reward_promotes_to_withdrawable",
  "events.max_retries",
  "notification.default_channels",
  "agent.tk_per_point",
];

export function ConfigVersionEditor({ initialValues, onSubmit, loading }: Props) {
  const [description, setDescription] = useState("");
  const [entries, setEntries] = useState<Entry[]>(() => {
    if (initialValues) {
      return Object.entries(initialValues).map(([k, v]) => ({
        key: k,
        value: typeof v === "object" ? JSON.stringify(v) : String(v),
        type: typeof v === "number" ? "number"
          : typeof v === "boolean" ? "boolean"
          : typeof v === "object" ? "json"
          : "string",
      }));
    }
    return [{ key: "", value: "", type: "string" }];
  });

  const parseValue = (e: Entry): unknown => {
    switch (e.type) {
      case "number":  return Number(e.value);
      case "boolean": return e.value === "true";
      case "json":    try { return JSON.parse(e.value); } catch { return e.value; }
      default:        return e.value;
    }
  };

  const submit = () => {
    const values: Record<string, unknown> = {};
    for (const e of entries) {
      if (!e.key.trim()) continue;
      values[e.key.trim()] = parseValue(e);
    }
    onSubmit(values, description);
  };

  return (
    <div className="space-y-4">
      <Input
        label="Version description"
        placeholder="e.g., Increase qualification threshold to 100 points"
        value={description}
        onChange={(e) => setDescription(e.target.value)}
      />

      <div className="space-y-2">
        {entries.map((e, i) => (
          <div key={i} className="grid grid-cols-[1fr_140px_1fr_40px] gap-2 items-start">
            <Input
              placeholder="config.key"
              value={e.key}
              list="config-keys"
              onChange={(evt) => setEntries((prev) => prev.map((p, idx) =>
                idx === i ? { ...p, key: evt.target.value } : p))}
            />
            <Select
              options={[
                { value: "string",  label: "String"  },
                { value: "number",  label: "Number"  },
                { value: "boolean", label: "Boolean" },
                { value: "json",    label: "JSON"    },
              ]}
              value={e.type}
              onChange={(evt) => setEntries((prev) => prev.map((p, idx) =>
                idx === i ? { ...p, type: evt.target.value as Entry["type"] } : p))}
            />
            {e.type === "boolean" ? (
              <Select
                options={[
                  { value: "true",  label: "true"  },
                  { value: "false", label: "false" },
                ]}
                value={e.value}
                onChange={(evt) => setEntries((prev) => prev.map((p, idx) =>
                  idx === i ? { ...p, value: evt.target.value } : p))}
              />
            ) : (
              <Input
                placeholder={e.type === "json" ? '[1,2,3]' : "value"}
                value={e.value}
                onChange={(evt) => setEntries((prev) => prev.map((p, idx) =>
                  idx === i ? { ...p, value: evt.target.value } : p))}
              />
            )}
            <button onClick={() => setEntries((prev) => prev.filter((_, idx) => idx !== i))}
                    className="h-10 w-10 flex items-center justify-center text-surface-500 hover:text-danger-600 hover:bg-danger-50 rounded-lg">
              <Trash2 className="h-4 w-4" />
            </button>
          </div>
        ))}
        <datalist id="config-keys">
          {KNOWN_KEYS.map((k) => <option key={k} value={k} />)}
        </datalist>
      </div>

      <div className="flex justify-between">
        <Button variant="outline" leftIcon={<Plus className="h-4 w-4" />}
                onClick={() => setEntries((p) => [...p, { key: "", value: "", type: "string" }])}>
          Add key
        </Button>
        <Button onClick={submit} loading={loading}
                disabled={!description.trim() || entries.every((e) => !e.key.trim())}>
          Publish new version
        </Button>
      </div>
    </div>
  );
}
